import React, { useState, type FC } from 'react';
import { ClockCircleOutlined } from '@ant-design/icons';
import { Button, Space, Text } from '@g2u/ui-kit';
import { Alert, Collapse, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { format, isValid, parse } from 'date-fns';
import he from 'he';

import { Condition, JobServiceTypeLabel, ServiceType } from 'types';
import type { Service, JsonDataItem, WithOptionalDataTest } from 'types';
import { Details, PopConfirm } from 'components';
import { Toast, isTimeExpired, extractServiceDetails } from 'helpers';
import { BookingStore } from 'store';
import styles from './job-confirmation.module.scss';

export namespace JobConfirmation {
  export type Props = {
    job: Service;
    onEdit?: () => void;
    style?: React.CSSProperties;
    openedKeys?: string[];
  } & WithOptionalDataTest;

  export const $: FC<Props> = observer((props) => {
    const { serviceSelection, serviceList, resetBookingProgress } = BookingStore;
    const { job, style, onEdit, openedKeys } = props;
    const [jobInfoTabActiveKey, setJobInfoTabActiveKey] = useState<string[]>(openedKeys ?? []);
    const navigate = useNavigate();
    const appointment = job?.job?.appointment || job?.products[0]?.service_technicians[0]?.appointment;

    const handleCollapseChange = (keys: string[] | string) => {
      setJobInfoTabActiveKey(Array.isArray(keys) ? keys : [keys]);
    };

    const serviceTypeTitles = {
      [ServiceType.OnSite]: 'On-site',
      [ServiceType.Remote]: 'Remote',
      [ServiceType.GeekCover]: 'GeekCover Remote',
    };

    const startTime = isValid(parse(appointment, 'yyyy-MM-dd HH:mm:ss', new Date()))
      ? format(parse(appointment, 'yyyy-MM-dd HH:mm:ss', new Date()), 'h:mmaaa | eeee do MMM y')
      : '';

    const { Link } = Typography;
    return (
      <div style={style}>
        <Alert
          message={<>Job #{job?.ID}</>}
          type={'info'}
          action={
            <>
              <PopConfirm.$
                title="Cancel this job"
                description={
                  <Space.$ direction={Space.Direction.Vertical}>
                    {isTimeExpired(job?.products[0].service_technicians[0].appointment, job?.customer.timezone, 24) ? (
                      <>
                        <Text.$>
                          There is less than 24 hours remaining before the appointment time. A 1-hour service fee will
                          be charged to the customer and credited to their account.
                        </Text.$>
                        <Text.$>Do you want to continue?</Text.$>
                      </>
                    ) : (
                      <>
                        <Text.$>
                          There is more than 24 hours remaining before the appointment time. The customer can cancel it
                          without any charge.
                        </Text.$>
                        <Text.$>Do you want to proceed?</Text.$>
                      </>
                    )}
                  </Space.$>
                }
                actions={[
                  {
                    label: 'No',
                  },
                  ...(isTimeExpired(job?.products[0].service_technicians[0].appointment, job?.customer.timezone, 24)
                    ? [
                        {
                          label: 'Yes, but waive the fee',
                          action: () => {
                            navigate(`/job/cancel/${job?.ID}`, {
                              state: { charges: Condition.No },
                            });
                            Toast.show({
                              description: <>Cancellation charge has been waived for this job.</>,
                              type: Toast.Type.Success,
                            });
                            resetBookingProgress();
                          },
                        },
                      ]
                    : []),
                  {
                    label: 'Yes',
                    action: () => {
                      navigate(`/job/cancel/${job?.ID}`, {
                        state: { charges: Condition.Yes },
                      });
                      resetBookingProgress();
                    },
                    type: Button.Type.Primary,
                  },
                ]}
              >
                <div>
                  <Button.$ size={Button.Size.Small} dataTestId="cancel-jobs" danger>
                    Cancel this job
                  </Button.$>
                </div>
              </PopConfirm.$>
            </>
          }
        />
        <Collapse
          activeKey={jobInfoTabActiveKey}
          onChange={handleCollapseChange}
          items={[
            {
              key: 'job',
              label: (
                <Space.$ size={Space.Size.ExtraSmall} align={Space.Align.Center} direction={Space.Direction.Horizontal}>
                  <b>Job Description</b> <Link>{jobInfoTabActiveKey.includes('job') ? 'Hide' : 'View'}</Link>
                </Space.$>
              ),
              children: (
                <Space.$ size={Space.Size.ExtraLarge} direction={Space.Direction.Vertical}>
                  <Space.$
                    size={Space.Size.ExtraLarge}
                    wrap
                    align={Space.Align.Start}
                    className={styles.confirmation__jobDetails}
                  >
                    <Details.$ title="Service Type" columns>
                      <Text.$>
                        {
                          JobServiceTypeLabel[
                            serviceSelection?.service_attributes.job_service_type as keyof typeof JobServiceTypeLabel
                          ]
                        }
                      </Text.$>

                      {/* TODO: Dynamic service from the location page currently not used */}
                    </Details.$>
                    <Details.$ title="Device(s)">
                      {serviceSelection?.product_selection_raw.devices.map((device: JsonDataItem) => (
                        <Details.Item
                          key={device.id}
                          label={serviceList?.parent.options.find((item: JsonDataItem) => item.id === device.id)?.text}
                        />
                      ))}
                    </Details.$>

                    <Details.$ title="Service and Payment Details">
                      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small}>
                        <Text.$>Operating System(s):</Text.$>
                        <Text.$ style={{ whiteSpace: 'break-spaces' }}>
                          {serviceSelection?.product_selection_raw.os
                            .map((item: JsonDataItem) => {
                              const value = Object.keys(item)[0];

                              const option = serviceList?.child
                                ?.find((child: { options?: JsonDataItem[] }) =>
                                  child.options?.some((opt: JsonDataItem) => opt.value === value),
                                )
                                ?.options?.find((opt: JsonDataItem) => opt.value === value);

                              return `- ${option?.label || value}`;
                            })
                            .join('\n')}
                        </Text.$>

                        {job?.products.map((item: JsonDataItem) => (
                          <Text.$ key={item.product.ID}>{item.product.name}</Text.$>
                        ))}

                        <Text.$>Total Min Price:</Text.$>
                        <Text.$>${job?.service_total}</Text.$>
                      </Space.$>
                    </Details.$>

                    <Details.$ title="Service Description">
                      <Text.$ style={{ whiteSpace: 'break-spaces' }}>
                        {extractServiceDetails(job?.service_description).problem}
                      </Text.$>
                    </Details.$>
                  </Space.$>
                </Space.$>
              ),
              showArrow: false,
            },
            {
              key: 'appointments',
              label: (
                <Space.$ size={Space.Size.ExtraSmall} align={Space.Align.Center} direction={Space.Direction.Horizontal}>
                  <b>Appointment Details</b>{' '}
                  <Link>{jobInfoTabActiveKey.includes('appointments') ? 'Hide' : 'View'}</Link>
                </Space.$>
              ),
              children: (
                <Space.$ size={Space.Size.ExtraLarge} direction={Space.Direction.Vertical}>
                  <Details.$ columns size={Details.Size.Full}>
                    <Details.Item
                      label={'On-site / Remote:'}
                      value={serviceTypeTitles[job?.service_type_ID?.toString() as ServiceType]}
                    />
                    <Details.Item
                      label={'Technician:'}
                      value={he.decode(job?.products[0].service_technicians[0].technician.name || '')}
                    />
                    <Details.Item
                      label={'Appointment Time:'}
                      value={
                        <>
                          <Text.$>{startTime}</Text.$>
                          <Button.$
                            icon={<ClockCircleOutlined />}
                            dataTestId="edit-customer-file-btn"
                            onClick={onEdit}
                            size={Button.Size.Small}
                            style={{ marginLeft: 12, marginTop: -10 }}
                            type={Button.Type.Primary}
                          >
                            Reschedule
                          </Button.$>
                        </>
                      }
                    />
                  </Details.$>
                </Space.$>
              ),
              showArrow: false,
            },
          ]}
        />
      </div>
    );
  });
}
